/**
 * Esri World Imagery for the terrain drape: the same tiles BaseMap.tsx draws,
 * stitched into one canvas over the grid's bounds, with the web-mercator maths
 * that maps any lon/lat to a texture coordinate on it.
 *
 * All or nothing: if any tile fails, or the set does not arrive within
 * TIMEOUT_MS, this resolves null and the terrain is drawn with its hypsometric
 * tint instead. A half-loaded mosaic would put black squares on the ground.
 */

export const IMAGERY_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile'
export const IMAGERY_ATTRIBUTION = 'Imagery © Esri, Maxar, Earthstar Geographics, and the GIS User Community'

const TILE = 256
const MAX_TILES_AXIS = 12
const MAX_ZOOM = 16
const TIMEOUT_MS = 20000

export interface Imagery {
  canvas: HTMLCanvasElement
  zoom: number
  x0: number
  y0: number
  tilesX: number
  tilesY: number
}

const lonToX = (lon: number, z: number) => ((lon + 180) / 360) * 2 ** z
const latToY = (lat: number, z: number) => {
  const r = (lat * Math.PI) / 180
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 2 ** z
}

export function texCoordFor(img: Pick<Imagery, 'zoom' | 'x0' | 'y0' | 'tilesX' | 'tilesY'>) {
  return (lon: number, lat: number): [number, number] => [
    (lonToX(lon, img.zoom) - img.x0) / img.tilesX,
    (latToY(lat, img.zoom) - img.y0) / img.tilesY,
  ]
}

export function tilePlan(bounds: [number, number, number, number]) {
  const [w, s, e, n] = bounds
  for (let z = MAX_ZOOM; z >= 1; z -= 1) {
    const x0 = Math.floor(lonToX(w, z))
    const x1 = Math.floor(lonToX(e, z))
    const y0 = Math.floor(latToY(n, z))
    const y1 = Math.floor(latToY(s, z))
    if (x1 - x0 + 1 <= MAX_TILES_AXIS && y1 - y0 + 1 <= MAX_TILES_AXIS) {
      return { zoom: z, x0, y0, tilesX: x1 - x0 + 1, tilesY: y1 - y0 + 1 }
    }
  }
  return { zoom: 1, x0: 0, y0: 0, tilesX: 2, tilesY: 2 }
}

function tile(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`tile failed: ${url}`))
    img.src = url
  })
}

export async function loadImagery(bounds: [number, number, number, number]): Promise<Imagery | null> {
  const plan = tilePlan(bounds)
  const canvas = document.createElement('canvas')
  canvas.width = plan.tilesX * TILE
  canvas.height = plan.tilesY * TILE
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  const jobs: Promise<void>[] = []
  for (let ty = 0; ty < plan.tilesY; ty += 1) {
    for (let tx = 0; tx < plan.tilesX; tx += 1) {
      const url = `${IMAGERY_URL}/${plan.zoom}/${plan.y0 + ty}/${plan.x0 + tx}`
      jobs.push(tile(url).then((img) => ctx.drawImage(img, tx * TILE, ty * TILE)))
    }
  }
  const timeout = new Promise<'timeout'>((resolve) => window.setTimeout(() => resolve('timeout'), TIMEOUT_MS))
  try {
    const outcome = await Promise.race([Promise.all(jobs), timeout])
    if (outcome === 'timeout') return null
    return { canvas, ...plan }
  } catch {
    return null
  }
}
